import { useCallback, useState } from 'react';
import { Textarea } from '@/shared/components/textarea';
import type { JustifiedActionInput } from '../../domain/entities/sales.entity';
import { useUploadSalesAttachment } from '../hooks/use-sales-actions';
import { getApiErrorMessage } from '../shared/display';

/**
 * Espelha a política de upload do módulo `sales` no backend
 * (`src/files/domain/file-upload-policy.ts`): PDF/JPEG/PNG até 20MB. O servidor
 * é quem manda — ele decide o tipo pelos magic bytes, não pelo que o navegador
 * declara, e recusa com 415/413. Isto aqui só evita a viagem: o operador
 * descobre ao escolher o arquivo, não depois de subir 20MB.
 *
 * Mudou lá? Muda aqui — divergir só custa uma mensagem de erro pior.
 */
const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
];
const MAX_SIZE_MB = 20;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

function validateAttachment(file: File): string | null {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return 'Formato inválido. Apenas PDF, JPG ou PNG são aceitos.';
  }
  if (file.size > MAX_SIZE_BYTES) {
    return `Arquivo muito grande. O tamanho máximo é ${MAX_SIZE_MB}MB.`;
  }
  return null;
}

/**
 * Estado do par "justificativa + anexo" que toda operação manual de vendas
 * exige. Mora num hook próprio porque o anexo é um **passo separado** no
 * backend: o arquivo sobe primeiro (`POST admin/sales/attachments`) e só o
 * `fileId` viaja no corpo da operação. Quem chama espera o `buildBody()` antes
 * de disparar a mutation — assim uma falha de upload não perde o texto digitado.
 */
export interface JustificationState {
  note: string;
  setNote: (value: string) => void;
  file: File | null;
  setFile: (file: File | null) => void;
  /**
   * `true` se ainda falta justificativa, ou se o anexo escolhido foi recusado —
   * o botão de confirmar fica travado.
   */
  isIncomplete: boolean;
  isUploading: boolean;
  uploadError: string | null;
  reset: () => void;
  /**
   * Sobe o anexo (se houver) e devolve o corpo da operação. `null` = o upload
   * falhou; a mensagem está em `uploadError` e nada deve ser disparado.
   */
  buildBody: () => Promise<JustifiedActionInput | null>;
}

export function useJustification(): JustificationState {
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [rejectedFile, setRejectedFile] = useState(false);
  const upload = useUploadSalesAttachment();

  /**
   * Arquivo recusado não vira `file`, mas **trava o confirmar** (`isIncomplete`):
   * sem isso a operação seguiria sem o anexo que o operador acabou de escolher,
   * silenciosamente. Ele limpa o estado escolhendo outro arquivo.
   */
  const selectFile = useCallback((selected: File | null) => {
    if (!selected) {
      setFile(null);
      setRejectedFile(false);
      setUploadError(null);
      return;
    }
    const error = validateAttachment(selected);
    setUploadError(error);
    setRejectedFile(error !== null);
    setFile(error === null ? selected : null);
  }, []);

  const reset = useCallback(() => {
    setNote('');
    setFile(null);
    setRejectedFile(false);
    setUploadError(null);
    upload.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildBody = useCallback(async () => {
    setUploadError(null);
    if (!file) return { note: note.trim() };
    try {
      const { fileId } = await upload.mutateAsync(file);
      return { note: note.trim(), attachmentFileId: fileId };
    } catch (err) {
      setUploadError(getApiErrorMessage(err, 'Falha ao enviar o anexo.'));
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, note]);

  return {
    note,
    setNote,
    file,
    setFile: selectFile,
    isIncomplete: note.trim() === '' || rejectedFile,
    isUploading: upload.isPending,
    uploadError,
    reset,
    buildBody,
  };
}

/**
 * Campos da justificativa. A justificativa é **obrigatória** em todas as
 * operações manuais de vendas (é ela que vira o `reason` do `entity_event`); o
 * anexo é opcional — comprovante de PIX, print do extrato.
 */
export function JustificationFields({
  state,
  label = 'Justificativa',
  placeholder,
  helperText,
  disabled = false,
}: {
  state: JustificationState;
  label?: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-4">
      <Textarea
        label={`${label} (obrigatória)`}
        value={state.note}
        onChange={state.setNote}
        placeholder={placeholder}
        helperText={
          helperText ??
          'Fica registrada no histórico da entidade, junto com quem executou.'
        }
        rows={3}
      />
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="sales-justification-attachment"
          className="text-(--color-text-primary) text-sm font-medium"
        >
          Anexo (opcional)
        </label>
        <input
          id="sales-justification-attachment"
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          disabled={disabled || state.isUploading}
          onChange={(e) => state.setFile(e.target.files?.[0] ?? null)}
          className="text-(--color-text-secondary) file:border-(--color-border) file:bg-(--color-bg-secondary) file:text-(--color-text-primary) hover:file:bg-(--color-bg-primary) w-full text-sm file:mr-3 file:cursor-pointer file:rounded-lg file:border file:px-3 file:py-1.5 file:text-sm"
        />
        <p className="text-(--color-text-secondary) text-xs">
          Comprovante do pagamento, print do extrato — o que sustentar a
          decisão. PDF, JPG ou PNG, até {MAX_SIZE_MB}MB.
        </p>
      </div>
      {state.uploadError && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {state.uploadError}
        </p>
      )}
    </div>
  );
}
